import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, EyeIcon, CheckIcon, CloudArrowUpIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { HAProxyInstance, HAProxyTemplate } from '../../types';
import api from '../../services/api';
import sseService from '../../services/sseService';

interface DeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  instances: HAProxyInstance[];
  templateId: string;
  templates: HAProxyTemplate[];
  onChangeTemplate: (id: string) => void;
}

enum DeploymentStep {
  CONFIRM,
  PROGRESS,
  COMPLETE
}

enum StepStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

interface DeploymentStepInfo {
  name: string;
  status: StepStatus;
  durationUs?: number;
  duration?: string;
  message: string;
}

const DEFAULT_STEPS: DeploymentStepInfo[] = [
  { name: 'Validating configuration', status: StepStatus.PENDING, message: 'Validating configuration...' },
  { name: 'Creating backup', status: StepStatus.PENDING, message: 'Creating backup...' },
  { name: 'Applying configuration', status: StepStatus.PENDING, message: 'Applying configuration...' },
  { name: 'Reloading HAProxy service', status: StepStatus.PENDING, message: 'Reloading HAProxy service...' },
  { name: 'Verifying service status', status: StepStatus.PENDING, message: 'Verifying service status...' }
];

// Helper function to format microsecond duration
const formatDurationUs = (durationUs?: number): string => {
  if (typeof durationUs !== 'number') return '';
  if (durationUs < 1000) {
    return `${durationUs}µs`;
  } else if (durationUs < 1_000_000) {
    return `${(durationUs / 1000).toFixed(1)}ms`;
  } else {
    return `${(durationUs / 1_000_000).toFixed(2)}s`;
  }
};

const DeploymentModal: React.FC<DeploymentModalProps> = ({ isOpen, onClose, instances, templateId, templates, onChangeTemplate }) => {
  const [step, setStep] = useState<DeploymentStep>(DeploymentStep.CONFIRM);
  const [progress, setProgress] = useState(0);
  const [currentInstance, setCurrentInstance] = useState<HAProxyInstance | null>(null);
  const [deploymentSteps, setDeploymentSteps] = useState<DeploymentStepInfo[]>([...DEFAULT_STEPS]);
  const [completedInstances, setCompletedInstances] = useState<string[]>([]);
  const [failedInstances, setFailedInstances] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const selectedTemplate = templates.find(t => t.id === templateId) || templates[0];

  // Function to handle deployment status updates
  const handleDeploymentStatus = useCallback((data: any) => {
    console.log('Received deployment status update:', data);
    
    const { instanceId, status, step, error, durationUs } = data;
    
    // Find the instance being updated
    const instance = instances.find(i => i.id === instanceId);
    if (!instance) return;
    
    // Update the current instance if it's the one being processed
    if (!currentInstance || currentInstance.id === instanceId) {
      setCurrentInstance(instance);
    }
    
    // Ensure step is within valid range (1-5)
    if (step < 1 || step > DEFAULT_STEPS.length) {
      console.warn(`Received invalid step number: ${step}`);
      return;
    }
    
    // Convert step to 0-based index
    const stepIndex = step - 1;
    
    // Update the current step
    setDeploymentSteps(prev => {
      const updated = [...prev];
      const currentStep = updated[stepIndex];
      
      if (status === 'in_progress') {
        updated[stepIndex] = {
          ...currentStep,
          status: StepStatus.IN_PROGRESS,
          durationUs: undefined,
          duration: undefined,
          message: currentStep.message
        };
      } else if (status === 'success') {
        updated[stepIndex] = {
          ...currentStep,
          status: StepStatus.COMPLETED,
          durationUs,
          duration: formatDurationUs(durationUs),
          message: 'Completed successfully'
        };
        
        // If there's a next step, mark it as in progress
        if (stepIndex + 1 < updated.length) {
          updated[stepIndex + 1] = {
            ...updated[stepIndex + 1],
            status: StepStatus.IN_PROGRESS,
            durationUs: undefined,
            duration: undefined,
          };
        }
      } else if (status === 'error') {
        updated[stepIndex] = {
          ...currentStep,
          status: StepStatus.FAILED,
          durationUs,
          duration: formatDurationUs(durationUs),
          message: error || 'Failed to complete step'
        };
      }
      
      return updated;
    });
    
    // Update progress
    setProgress(Math.round((step / DEFAULT_STEPS.length) * 100));
    
    // If deployment is complete for this instance
    if (status === 'success' || status === 'error') {
      if (status === 'success') {
        setCompletedInstances(prev => {
          if (!prev.includes(instanceId)) {
            return [...prev, instanceId];
          }
          return prev;
        });
        
        // If this is the final step and it's successful, transition to complete
        if (step === DEFAULT_STEPS.length) {
          setProgress(100);
          setCurrentInstance(null);
          setStep(DeploymentStep.COMPLETE);
        }
      } else {
        setFailedInstances(prev => {
          if (!prev.includes(instanceId)) {
            return [...prev, instanceId];
          }
          return prev;
        });
      }
    }
  }, [currentInstance, instances]);

  // Register and unregister event handler
  useEffect(() => {
    if (step === DeploymentStep.PROGRESS) {
      sseService.on('deployment_status', handleDeploymentStatus);
      return () => {
        sseService.off('deployment_status', handleDeploymentStatus);
      };
    }
  }, [step, handleDeploymentStatus]);

  // Function to start the deployment process
  const startDeployment = async () => {
    setStep(DeploymentStep.PROGRESS);
    setProgress(0);
    
    try {
      // Call the deployment API with the template ID
      await api.config.deploy(templateId);
    } catch (error) {
      console.error('Deployment failed:', error);
      setDeploymentSteps(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { ...updated[updated.length - 1], status: StepStatus.FAILED };
        return updated;
      });
      setFailedInstances(prev => [...prev, currentInstance?.id || '']);
      setStep(DeploymentStep.COMPLETE);
    }
  };

  // Function to reset the modal state when closed
  const handleClose = () => {
    onClose();
    // Reset state after animation completes
    setTimeout(() => {
      setStep(DeploymentStep.CONFIRM);
      setProgress(0);
      setCurrentInstance(null);
      setDeploymentSteps([...DEFAULT_STEPS]);
      setCompletedInstances([]);
      setFailedInstances([]);
    }, 300);
  };

  // Helper to check if any step has failed
  const hasError = deploymentSteps.some(step => step.status === StepStatus.FAILED);

  // Template selection modal
  const renderTemplateModal = () => (
    <Transition.Root show={showTemplateModal} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowTemplateModal(false)}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-zinc-900 text-left shadow-xl transition-all">
                <div className="flex justify-between items-center px-6 py-4 border-b border-zinc-800">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-indigo-400 flex items-center gap-2">
                    Choose Template
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                    onClick={() => setShowTemplateModal(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="px-6 py-4 max-h-80 overflow-y-auto">
                  <ul className="divide-y divide-zinc-800">
                    {templates.map(t => (
                      <li key={t.id} className="py-3 flex items-center justify-between cursor-pointer hover:bg-zinc-800/50 rounded transition-colors"
                        onClick={() => {
                          onChangeTemplate(t.id);
                          setShowTemplateModal(false);
                        }}
                      >
                        <div className="flex items-center">
                          <span className={`text-sm font-medium ${t.id === templateId ? 'text-indigo-400' : 'text-zinc-100'}`}>{t.name}{t.isDefault ? <span className="ml-2 text-xs text-green-400"> (default)</span> : null}</span>
                        </div>
                        {t.id === templateId && <CheckIcon className="h-5 w-5 text-indigo-400" />}
                      </li>
                    ))}
                  </ul>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );

  // Render the confirmation step
  const renderConfirmStep = () => (
    <div className="p-6">
      <div className="flex flex-col items-center">
        <CloudArrowUpIcon className="h-12 w-12 text-blue-500 mb-4 mx-auto" />
        <h3 className="text-lg font-medium text-zinc-100 mb-2">Deploy Configuration</h3>
        <p className="text-sm text-zinc-400 mb-4 text-center">
          You are about to deploy <span className="font-semibold text-zinc-100">{selectedTemplate?.name}</span> to all <span className="font-semibold text-zinc-100">{instances.length}</span> HAProxy instance{instances.length === 1 ? '' : 's'}.
          This action will reload the HAProxy service on each instance.
        </p>
        {/* Template section title and selection row */}
        <div className="w-full flex flex-col items-center mb-4">
          <div
            className="flex items-center justify-center gap-2 bg-gradient-to-br from-zinc-800/90 via-zinc-800/70 to-zinc-900/80 border border-blue-400/20 hover:border-blue-500/60 rounded-2xl px-6 py-4 shadow-lg transition-all duration-200 cursor-pointer group"
            style={{ minWidth: 320 }}
            onClick={() => setShowTemplateModal(true)}
          >
            <DocumentDuplicateIcon className="h-5 w-5 text-blue-400 mr-1" />
            <span className={`text-lg font-semibold text-zinc-100 flex items-center`}>{selectedTemplate?.name}{selectedTemplate?.isDefault ? <span className="ml-2 text-xs text-green-400 font-medium">(default)</span> : null}</span>
          </div>
          <span className="text-xs text-zinc-400 mt-2">Click to select template</span>
        </div>
      </div>
      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
          onClick={startDeployment}
        >
          Deploy
        </button>
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md bg-zinc-700 px-3 py-2 text-sm font-semibold text-zinc-300 shadow-sm hover:bg-zinc-600 sm:col-start-1 sm:mt-0"
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Status icon based on step status
  const getStepStatusIcon = (status: StepStatus) => {
    switch (status) {
      case StepStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-emerald-500" />;
      case StepStatus.IN_PROGRESS:
        return (
          <ArrowPathIcon className="h-5 w-5 text-sky-500 animate-spin" />
        );
      case StepStatus.FAILED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-rose-500" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-zinc-700"></div>;
    }
  };

  // Render the step content
  const renderStepContent = (step: DeploymentStepInfo) => (
    <div className="ml-4 flex-grow min-w-0">
      <div className="text-sm font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors mb-1">
        {step.name}
      </div>
      {step.status === StepStatus.FAILED && (
        <div className="flex items-center text-xs text-rose-500 mt-1 w-full">
          <span className="truncate flex-1" title={step.message}>{step.message}</span>
          <button
            type="button"
            className="ml-2 p-1 rounded hover:bg-rose-900/30 focus:outline-none"
            onClick={() => { setExpandedError(step.message); setShowErrorModal(true); }}
            aria-label="Expand error message"
          >
            <EyeIcon className="h-4 w-4 text-rose-400" />
          </button>
        </div>
      )}
    </div>
  );

  // Render the progress step
  const renderProgressStep = () => (
    <div className="p-6">
      {hasError ? (
        <div className="text-center mb-6">
          <ExclamationTriangleIcon className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-rose-200 mb-2">Deployment Failed</h3>
        </div>
      ) : (
        <>
          <h3 className="text-lg font-medium text-zinc-100 mb-2">Deployment in Progress</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Deploying configuration to {instances.length} instance(s)
          </p>
        </>
      )}
      
      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex justify-between mb-1">
          <span className="text-xs font-medium text-zinc-400">Overall Progress</span>
          <span className="text-xs font-medium text-zinc-400">{progress}%</span>
        </div>
        <div className="w-full bg-zinc-700 rounded-full h-2">
          <div 
            className="bg-indigo-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
      
      {/* Current Instance */}
      {currentInstance && !hasError && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-zinc-200 mb-2">
            Instance: {currentInstance.name} ({currentInstance.host})
          </h4>
        </div>
      )}
      
      {/* Step-by-Step Progress */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-zinc-200 mb-2">Progress:</h4>
        <div className="space-y-5">
          {deploymentSteps.map((deploymentStep, index) => (
            <div key={index} className="flex items-start group">
              <div className="flex-shrink-0 w-6 pt-0.5">
                {getStepStatusIcon(deploymentStep.status)}
              </div>
              {renderStepContent(deploymentStep)}
              <div className="flex-shrink-0 ml-4 pt-0.5">
                {deploymentStep.duration && (
                  <span className="text-xs font-medium text-zinc-500">{deploymentStep.duration}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 sm:mt-6">
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-zinc-700 px-3 py-2 text-sm font-semibold text-zinc-300 shadow-sm hover:bg-zinc-600"
          onClick={handleClose}
        >
          {hasError ? 'Close' : 'Cancel Deployment'}
        </button>
      </div>
    </div>
  );

  // Render the complete step
  const renderCompleteStep = () => (
    <div className="p-6">
      <div className="text-center mb-6">
        {failedInstances.length === 0 ? (
          <>
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-100 mb-2">Deployment Complete</h3>
            <p className="text-sm text-zinc-400">
              Configuration successfully deployed to all {instances.length} instance(s).
            </p>
          </>
        ) : (
          <>
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-100 mb-2">Deployment Partially Complete</h3>
            <p className="text-sm text-zinc-400">
              Configuration deployed to {completedInstances.length} of {instances.length} instance(s).
            </p>
          </>
        )}
      </div>
      
      {/* Deployment Steps */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-zinc-200 mb-6">Deployment Steps:</h4>
        <div className="space-y-5">
          {deploymentSteps.map((step, index) => (
            <div key={index} className="flex items-start group">
              <div className="flex-shrink-0 w-6 pt-0.5">
                {getStepStatusIcon(step.status)}
              </div>
              {renderStepContent(step)}
              {step.duration && (
                <div className="flex-shrink-0 ml-4 pt-0.5">
                  <span className="text-xs font-medium text-zinc-500">{step.duration}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Instances Summary */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-zinc-200 mb-2">Deployment Summary:</h4>
        <div className="bg-zinc-800 rounded-md p-3 max-h-48 overflow-auto">
          {instances.map(instance => {
            const isSuccessful = completedInstances.includes(instance.id);
            const isFailed = failedInstances.includes(instance.id);
            
            return (
              <div key={instance.id} className="flex items-center py-1 border-b border-zinc-700 last:border-0">
                <div className={`w-2 h-2 rounded-full mr-2 ${isSuccessful ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                <span className="text-sm text-zinc-300">{instance.name}</span>
                <span className="ml-auto text-xs text-zinc-500">
                  {isSuccessful ? 'Success' : isFailed ? 'Failed' : 'Pending'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        {failedInstances.length > 0 && (
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 sm:col-start-2"
            onClick={() => {
              // Reset failed instances and restart deployment for them
              setStep(DeploymentStep.CONFIRM);
            }}
          >
            Retry Failed
          </button>
        )}
        <button
          type="button"
          className={`mt-3 inline-flex w-full justify-center rounded-md bg-zinc-700 px-3 py-2 text-sm font-semibold text-zinc-300 shadow-sm hover:bg-zinc-600 ${
            failedInstances.length > 0 ? 'sm:col-start-1 sm:mt-0' : 'sm:col-span-2'
          }`}
          onClick={handleClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  // Error modal for expanded error message
  const renderErrorModal = () => (
    <Transition.Root show={showErrorModal} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowErrorModal(false)}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-zinc-900 text-left shadow-xl transition-all">
                <div className="flex justify-between items-center px-6 py-4 border-b border-zinc-800">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-rose-400 flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-6 w-6 text-rose-400" />
                    Error Details
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                    onClick={() => setShowErrorModal(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="px-6 py-4 max-h-[60vh] overflow-auto">
                  <pre className="whitespace-pre-wrap font-mono text-xs text-rose-200">
                    {expandedError}
                  </pre>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );

  return (
    <>
      {showTemplateModal ? (
        renderTemplateModal()
      ) : (
        <Transition.Root show={isOpen} as={React.Fragment}>
          <Dialog as="div" className="relative z-50" onClose={handleClose}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-zinc-900 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="absolute right-0 top-0 pr-4 pt-4">
                      <button
                        type="button"
                        className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                        onClick={handleClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    
                    {step === DeploymentStep.CONFIRM && renderConfirmStep()}
                    {step === DeploymentStep.PROGRESS && renderProgressStep()}
                    {step === DeploymentStep.COMPLETE && renderCompleteStep()}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      {renderErrorModal()}
    </>
  );
};

export default DeploymentModal; 